import React, { useCallback } from 'react';
import {
  Entity,
  RELATION_OWNED_BY,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  EntityLifecycleFilter,
  EntityLifecyclePicker,
  EntityNamespaceFilter,
  EntityNamespacePicker,
  EntityOwnerFilter,
  EntityOwnerPicker,
  EntityTagFilter,
  EntityTagPicker,
  catalogReactTranslationRef,
  entityPresentationApiRef,
  entityPresentationSnapshot,
  getEntityRelations,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { FacetedAutocompletePicker } from './FacetedAutocompletePicker';

const getLifecycle = (entity: Entity) =>
  typeof entity.spec?.lifecycle === 'string' ? [entity.spec.lifecycle] : [];

const getTags = (entity: Entity) => entity.metadata.tags ?? [];

const getNamespace = (entity: Entity) =>
  entity.metadata.namespace ? [entity.metadata.namespace] : [];

const getOwners = (entity: Entity) =>
  getEntityRelations(entity, RELATION_OWNED_BY).map(owner =>
    stringifyEntityRef(owner),
  );

export const FacetedEntityLifecyclePicker = () => {
  const { t } = useTranslationRef(catalogReactTranslationRef);
  return (
    <FacetedAutocompletePicker
      label={t('entityLifecyclePicker.title')}
      name="lifecycles"
      getValues={getLifecycle}
      Filter={EntityLifecycleFilter}
      showCounts
      fallback={<EntityLifecyclePicker />}
    />
  );
};

export const FacetedEntityTagPicker = () => {
  const { t } = useTranslationRef(catalogReactTranslationRef);
  return (
    <FacetedAutocompletePicker
      label={t('entityTagPicker.title')}
      name="tags"
      getValues={getTags}
      Filter={EntityTagFilter}
      showCounts
      fallback={<EntityTagPicker />}
    />
  );
};

export const FacetedEntityNamespacePicker = () => {
  const { t } = useTranslationRef(catalogReactTranslationRef);
  return (
    <FacetedAutocompletePicker
      label={t('entityNamespacePicker.title')}
      name="namespace"
      getValues={getNamespace}
      Filter={EntityNamespaceFilter}
      showCounts
      fallback={<EntityNamespacePicker />}
    />
  );
};

export const FacetedEntityOwnerPicker = () => {
  const { t } = useTranslationRef(catalogReactTranslationRef);
  const { filters } = useEntityList();
  const entityPresentationApi = useApi(entityPresentationApiRef);
  const getOwnerLabel = useCallback(
    (ref: string) =>
      entityPresentationSnapshot(ref, undefined, entityPresentationApi)
        .primaryTitle,
    [entityPresentationApi],
  );

  if (['user', 'group'].includes(filters.kind?.value.toLowerCase() ?? '')) {
    return null;
  }

  return (
    <FacetedAutocompletePicker
      label={t('entityOwnerPicker.title')}
      name="owners"
      getValues={getOwners}
      Filter={EntityOwnerFilter}
      getOptionLabel={getOwnerLabel}
      showCounts
      fallback={<EntityOwnerPicker />}
    />
  );
};
